/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, type ReactNode } from 'react'
import type { DrilldownHierarchyNode } from './drilldownHierarchy'
import type {
  DrilldownWidgetInput,
  ExportWidgetDataInput,
  FetchSlicerOptionsInput,
  HydratedWidgetResult,
  HydrateWidgetInput,
} from './widgetData'

/**
 * Where a dashboard's numbers come from. The app's calls the API; the Claude
 * viewer's goes through Claude to the connector's tools. Each takes and returns
 * the same shapes, so the viewer doesn't know which it has.
 */
export type DashboardDataSource = {
  hydrateWidget: (input: HydrateWidgetInput) => Promise<HydratedWidgetResult>
  slicerOptions: (input: FetchSlicerOptionsInput) => Promise<string[]>
  /** Left out where drilling isn't offered; the viewer then doesn't offer it. */
  drilldownWidget?: (input: DrilldownWidgetInput) => Promise<HydratedWidgetResult>
  /** Left out where downloads aren't possible; the viewer then hides Export. */
  exportWidget?: (input: ExportWidgetDataInput) => Promise<{ blob: Blob; filename: string }>
  /** A hook, called on every render of a widget, so it must stay the same function. */
  useDrilldownHierarchy: (clientId: string, platformTypeId: number) => DrilldownHierarchyNode[] | null
}

const DashboardDataSourceContext = createContext<DashboardDataSource | null>(null)

export function DashboardDataSourceProvider({ value, children }: { value: DashboardDataSource; children: ReactNode }) {
  return <DashboardDataSourceContext.Provider value={value}>{children}</DashboardDataSourceContext.Provider>
}

/** No default on purpose: a default would import the app's API, and with it the HTTP client, into the Claude bundle. */
export function useDashboardDataSource(): DashboardDataSource {
  const dataSource = useContext(DashboardDataSourceContext)
  if (!dataSource) throw new Error('DashboardDataSourceProvider is missing above this dashboard.')
  return dataSource
}
